import { useState } from 'react';
import { Flex, Spinner } from '@applyboard/crystal-ui';
import { useFeatureDecision } from '@applyboard/ui-utils';
import { FeatureFlag } from '../../../src/FeatureFlags';
import ApplicationForm from './ApplicationForm';
import ConfirmationStep from './ConfirmationStep/ConfirmationStep';
import ConsentForm from './ConsentForm';
import { RBC_GIC_FORM_DEFAULT_VALUES } from './FormStateMapper';
import Stepper from './Stepper';
import VerificationForm from './VerificationForm';
import { RbcGicFormTypeV2 } from './types';

interface RbcGicFormProps {
  studentId?: string | undefined;
  preFillFormState?: RbcGicFormTypeV2;
  isStudentDetailsError?: boolean;
  isCurrentUserApplyingForThemselves?: boolean;
  validateValuesOnLoad?: boolean;
}

const setMissingPrefillVals = (prefillFormState?: RbcGicFormTypeV2): RbcGicFormTypeV2 => {
  if (!prefillFormState) {
    return RBC_GIC_FORM_DEFAULT_VALUES;
  }
  return {
    ...RBC_GIC_FORM_DEFAULT_VALUES,
    ...prefillFormState,
  };
};

const RbcGicForm = ({
  studentId = '',
  preFillFormState,
  isStudentDetailsError,
  isCurrentUserApplyingForThemselves,
  validateValuesOnLoad,
}: RbcGicFormProps) => {
  const [formState, setFormState] = useState<RbcGicFormTypeV2>(
    setMissingPrefillVals(preFillFormState),
  );

  const [decisionEngine, decisionEngineReady] = useFeatureDecision();

  const isVasRbcGicExtendedCountriesEnabled =
    decisionEngine?.isFeatureEnabled(FeatureFlag.VasRbcGicExtendedCountries) ?? false;

  const createSteps = (goNext: () => void, goBack: () => void) => [
    <ApplicationForm
      studentId={studentId}
      key="step1"
      goNext={goNext}
      goBack={goBack}
      formState={formState}
      setFormState={setFormState}
      isStudentDetailsError={isStudentDetailsError}
      isCurrentUserApplyingForThemselves={isCurrentUserApplyingForThemselves}
      validateValuesOnLoad={validateValuesOnLoad}
      isVasRbcGicExtendedCountriesEnabled={isVasRbcGicExtendedCountriesEnabled}
    />,
    <VerificationForm
      key="step3"
      goNext={goNext}
      goBack={goBack}
      formState={formState}
      setFormState={setFormState}
    />,
    <ConsentForm
      key="step3"
      goNext={goNext}
      goBack={goBack}
      formState={formState}
      setFormState={setFormState}
    />,
    <ConfirmationStep key="step4" formState={formState} />,
  ];

  if (!decisionEngineReady) {
    return (
      <Flex align="center" justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Stepper totalSteps={4}>
      {({ currentStep, goNext, goBack }) => {
        const steps = createSteps(goNext, goBack);
        return <>{steps[currentStep]}</>;
      }}
    </Stepper>
  );
};

export default RbcGicForm;
